// Web Shop Manager sign-in: Clerk as the OpenID Connect provider behind Saleor's own
// external authentication (wsm-saleor, wsm/auth/oidc.py; Bill, 2026-09-28).
//
// The dashboard runs on the client's own domain, where Clerk's browser SDK cannot
// (a Clerk instance is bound to one root domain). So nothing here loads Clerk. The
// tenant's API lists one external authentication when the door is open; the sign-in
// route sends the browser to it, Clerk's hosted screens do the rest, and the
// authorization code comes back through Saleor's stock callback route. Everything a
// person sees during sign-in is Clerk's, and adding a method is a toggle there.
import { getClerkPublishableKey } from "@dashboard/config";
import { useAvailableExternalAuthenticationsQuery } from "@dashboard/graphql";

/** The plugin id the tenant's API answers with (wsm/auth/oidc.py, PLUGIN_ID). */
export const WSM_CLERK_PLUGIN_ID = "wsm.auth.clerk";

/**
 * Set for the life of the tab when the person signed out on purpose. The sign-in route
 * reads it so a deliberate sign-out lands on a page with a button rather than being sent
 * straight back into Clerk, which still holds its own session and would sign the same
 * person in again without a keystroke.
 */
const SIGNED_OUT_KEY = "wsmSignedOut";

export const markSignedOut = (): void => {
  try {
    window.sessionStorage.setItem(SIGNED_OUT_KEY, "1");
  } catch {
    // Storage blocked: the sign-in route will redirect, which is the worse of the two
    // outcomes only on a shared computer, and only until the Clerk session ends.
  }
};

export const clearSignedOut = (): void => {
  try {
    window.sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // As above.
  }
};

export const wasSignedOut = (): boolean => {
  try {
    return window.sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * PKCE (RFC 7636). The verifier is minted here, kept in this tab's sessionStorage, and
 * never sent anywhere but the tenant's API at the code exchange; its S256 challenge rides
 * in the authorization request. So a code that leaks out of the callback URL, or one an
 * attacker obtained for themselves and planted in this tab's callback, exchanges for
 * nothing: the API refuses any exchange whose verifier does not match the challenge it
 * sealed in the state (wsm/auth/oidc.py), and Clerk checks the same binding.
 */
const PKCE_VERIFIER_KEY = "wsmPkceVerifier";

const base64url = (bytes: Uint8Array): string =>
  window
    .btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/**
 * Mint a verifier for a sign-in that is starting, keep it for the callback, and return
 * the challenge to send with the authorization request. Storage blocked: the verifier is
 * lost, the exchange is refused in the API's words, and the person is asked to start
 * again, which is the right outcome for a tab that cannot keep a secret.
 */
export const startPkce = async (): Promise<string> => {
  const random = new Uint8Array(32);

  window.crypto.getRandomValues(random);

  const verifier = base64url(random);
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));

  try {
    window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  } catch {
    // As above.
  }

  return base64url(new Uint8Array(digest));
};

/** The verifier the current sign-in was started with, once; null when this tab has none. */
export const takePkceVerifier = (): string | null => {
  try {
    const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);

    window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);

    return verifier;
  } catch {
    return null;
  }
};

export interface WsmClerkTenantState {
  /** This tenant's API offers the Web Shop Manager sign-in. */
  enabled: boolean;
  /** The API has not answered yet. */
  loading: boolean;
}

/**
 * Whether this tenant signs staff in through Web Shop Manager. The tenant's own API is
 * the only thing that says so: one image serves every tenant, and the door is a
 * fleet-wide switch narrowed per tenant on the API side. The query is anonymous and
 * cached by Apollo, so every caller after the first reads the same answer.
 */
export const useWsmClerkTenant = (): WsmClerkTenantState => {
  const { data, loading } = useAvailableExternalAuthenticationsQuery({
    fetchPolicy: "cache-first",
  });
  const enabled = (data?.shop?.availableExternalAuthentications ?? []).some(
    auth => auth.id === WSM_CLERK_PLUGIN_ID,
  );

  return { enabled, loading: loading && !data };
};

/**
 * Clerk's account portal for this instance, where passkeys, two-step and the password
 * are managed. Derived from the publishable key, which base64-encodes the instance's
 * Frontend API host: `clerk.webshopmanager.com` on the production instance, whose
 * portal is `accounts.webshopmanager.com`, and `<slug>.clerk.accounts.dev` on a
 * development one, whose portal is `<slug>.accounts.dev`. Both are the Frontend API
 * host with its `clerk` label taken out, which is how Clerk names them.
 */
export const getAccountPortalUrl = (): string => {
  const key = getClerkPublishableKey();
  const encoded = key.replace(/^pk_(test|live)_/, "");

  if (!encoded) {
    return "";
  }

  try {
    const frontendApi = window.atob(encoded).replace(/\$$/, "");

    if (frontendApi.startsWith("clerk.")) {
      return `https://${frontendApi.replace(/^clerk\./, "accounts.")}/user`;
    }

    if (frontendApi.includes(".clerk.")) {
      return `https://${frontendApi.replace(".clerk.", ".")}/user`;
    }

    return "";
  } catch {
    return "";
  }
};
